import * as React from "react"

import { cn } from "@/lib/utils"

export interface InputProps extends React.ComponentProps<"input"> {
  leftIcon?: React.ReactNode
  error?: string
  label?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, leftIcon, error, label, id: idProp, ...props }, ref) => {
    const autoId = React.useId()
    const id = idProp ?? autoId

    return (
      // Neither wrapper is positioned unless it draws its own icon: a
      // positioned wrapper paints over icons that callers place beside the
      // field (search glass, show-password eye), hiding them.
      <div className="w-full">
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-semibold text-foreground mb-1.5"
          >
            {label}
          </label>
        )}
        <div className={leftIcon ? "relative" : undefined}>
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none flex items-center justify-center">
              {leftIcon}
            </span>
          )}
          <input
            id={id}
            type={type}
            className={cn(
              // text-base (16px) on mobile prevents iOS Safari auto-zoom on focus; md:text-sm keeps the desktop density.
              "h-11 w-full rounded-lg border-2 border-input bg-card shadow-e1 px-3 text-base md:text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:border-ring hover:border-ring/50 transition-colors disabled:cursor-not-allowed disabled:opacity-50 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
              leftIcon && "pl-10",
              error && "border-destructive focus-visible:ring-destructive",
              className
            )}
            spellCheck={
              type === "text" || type === "search"
                ? props.spellCheck !== false
                : false
            }
            ref={ref}
            {...props}
          />
        </div>
        {error && (
          <p className="text-sm text-destructive mt-1">{error}</p>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
